import { type ChangeEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Language, LANGUAGES, MATERIAL_MAX_CHARS } from '@quizdb/common';
import { problemCode } from '../api/problemCode';
import { Icon } from '../common/Icon';
import {
  ExtractionProgress,
  MaterialPrepError,
  MaterialPrepPhase,
  OcrProgress,
  PreparedMaterial,
} from './extraction/extractionTypes';
import { extractPdf } from './extraction/extractPdf';
import { hashFile } from './extraction/hashFile';
import { openPdfRenderer } from './extraction/pdfRenderer';
import { useCreateExtractionUploadMutation, useLazyGetExtractionQuery } from './jobEndpoints';
import { ocrPdfPages } from './ocr/ocrPdfPages';
import { OcrEngine } from './ocr/ocrTypes';
import { createTesseractOcr } from './ocr/tesseractOcr';
import { PagePreview } from './PagePreview';
import { prepareMaterial, PrepareMaterialDeps } from './prepareMaterial';

/** The page range as typed: two number inputs' raw values, blank meaning the PDF's edge (QZ-D81). */
export interface PageRangeInput {
  from: string;
  to: string;
}

/** A whole, positive number typed into a page input, or `undefined` for anything else. */
const parsePage = (value: string): number | undefined => {
  const trimmed = value.trim();
  return /^[0-9]+$/.test(trimmed) ? Number(trimmed) : undefined;
};

/**
 * The typed range as page numbers, with a blank `from` meaning 1 and a blank `to` meaning the
 * last page (QZ-D81), or `undefined` unless `1 ≤ from ≤ to ≤ pdfPageCount`.
 */
export const parseRange = (
  range: PageRangeInput,
  pdfPageCount: number
): { from: number; to: number } | undefined => {
  const from = range.from.trim() === '' ? 1 : parsePage(range.from);
  const to = range.to.trim() === '' ? pdfPageCount : parsePage(range.to);
  if (from === undefined || to === undefined) {
    return undefined;
  }
  return from >= 1 && from <= to && to <= pdfPageCount ? { from, to } : undefined;
};

/** The kept characters on pages `from`–`to` (1-based, inclusive) (PTX-D3). */
export const charsInRange = (pageChars: readonly number[], from: number, to: number): number =>
  pageChars.slice(from - 1, to).reduce((sum, chars) => sum + chars, 0);

export interface PdfMaterialSectionProps {
  value: PreparedMaterial | undefined;
  onChange: (material: PreparedMaterial | undefined) => void;
  range: PageRangeInput;
  onRangeChange: (range: PageRangeInput) => void;
  /** The material field's error from the form: a zod issue or a server problem code. */
  error?: string;
  disabled?: boolean;
  /**
   * The default page language for reading scanned pages by OCR (PHO-D1); `'sv'`, New Job's own
   * default job language, when absent (QZ-D22).
   */
  jobLanguage?: Language;
}

interface Status {
  phase: MaterialPrepPhase;
  progress?: ExtractionProgress | OcrProgress;
}

const isAbortError = (thrown: unknown): boolean =>
  thrown instanceof DOMException && thrown.name === 'AbortError';

const abortError = (): DOMException => new DOMException('Aborted', 'AbortError');

const noop = () => undefined;

/**
 * Creates the OCR engine for `language`, raced against `signal`: creation cannot be cancelled
 * and may never settle, so an abort rejects at once, and an engine arriving after the abort is
 * terminated unused (PHO-D10).
 */
const createEngine = (language: Language, signal: AbortSignal): Promise<OcrEngine> =>
  new Promise<OcrEngine>((resolve, reject) => {
    if (signal.aborted) {
      reject(abortError());
      return;
    }
    const onAbort = () => reject(abortError());
    signal.addEventListener('abort', onAbort);
    createTesseractOcr(language).then(
      (engine) => {
        signal.removeEventListener('abort', onAbort);
        if (signal.aborted) {
          engine.terminate().catch(noop);
          reject(abortError());
        } else {
          resolve(engine);
        }
      },
      (thrown: unknown) => {
        signal.removeEventListener('abort', onAbort);
        reject(thrown);
      }
    );
  });

/**
 * Reads a PDF's scanned pages by OCR (QZ-D92) on an engine of its own, terminated once the
 * reading ends in any way, so an aborted run leaves no Tesseract worker running (PHO-D10).
 */
const readOcrPages: PrepareMaterialDeps['ocrPages'] = async (
  file,
  pages,
  language,
  onProgress,
  signal
) => {
  const engine = await createEngine(language, signal);
  try {
    return await ocrPdfPages(
      file,
      pages,
      { openRenderer: openPdfRenderer, ocr: engine },
      onProgress,
      signal
    );
  } finally {
    void engine.terminate().catch(noop);
  }
};

/**
 * New Job's PDF material: picks a PDF, turns it into text in this browser (QZ-D80) or finds its
 * earlier extraction (QZ-D83), then shows its pages and characters, an optional page range
 * (QZ-D81) and a preview of any page's kept text (QZ-D82). Pages with almost no text layer are
 * read by OCR in the page language, whose select shows only once OCR runs (QZ-D92, PHO-D1).
 * Picking another file, changing the page language, or leaving the page aborts the run in
 * progress (PTX-D9).
 */
export const PdfMaterialSection = ({
  value,
  onChange,
  range,
  onRangeChange,
  error,
  disabled,
  jobLanguage,
}: PdfMaterialSectionProps) => {
  const { t, i18n } = useTranslation();
  const [getExtraction] = useLazyGetExtractionQuery();
  const [createExtractionUpload] = useCreateExtractionUploadMutation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const controllerRef = useRef<AbortController | undefined>(undefined);
  // Bumped on each run, from a pick or a page-language change, so only the latest run's phases
  // and result reach the state (PTX-D9).
  const pickIdRef = useRef(0);

  const [preparing, setPreparing] = useState<{ name: string; status: Status } | undefined>();
  const [prepError, setPrepError] = useState<string | undefined>();
  // The language scanned pages are read in; each pick resets it to the job language (PHO-D1).
  const [pageLanguage, setPageLanguage] = useState<Language>(jobLanguage ?? 'sv');
  // Set once a run reaches OCR; a new pick hides the select again (PHO-D1).
  const [pageLanguageShown, setPageLanguageShown] = useState(false);
  const lastFileRef = useRef<File | undefined>(undefined);

  useEffect(() => () => controllerRef.current?.abort(), []);

  const run = async (file: File, language: Language) => {
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    const pickId = ++pickIdRef.current;
    const isLatest = () => pickIdRef.current === pickId;

    setPrepError(undefined);
    setPreparing({ name: file.name, status: { phase: 'hashing' } });
    onChange(undefined);

    try {
      const material = await prepareMaterial(
        file,
        {
          hash: hashFile,
          extract: extractPdf,
          lookup: (id) =>
            getExtraction(id)
              .unwrap()
              .catch((thrown: unknown) =>
                problemCode(thrown) === 'ExtractionNotFound' ? undefined : Promise.reject(thrown)
              ),
          createUpload: (body) => createExtractionUpload(body).unwrap(),
          ocrPages: readOcrPages,
        },
        (phase, progress) => {
          if (!isLatest()) {
            return;
          }
          if (phase === 'reading') {
            setPageLanguageShown(true);
          }
          // Extracting and reading are shown once they report progress, with a page count.
          if ((phase === 'extracting' || phase === 'reading') && !progress) {
            return;
          }
          setPreparing((prev) => prev && { ...prev, status: { phase, progress } });
        },
        controller.signal,
        language
      );
      if (isLatest()) {
        onChange(material);
        onRangeChange({ from: '1', to: String(material.pdfPageCount) });
      }
    } catch (thrown) {
      if (isLatest() && !isAbortError(thrown)) {
        const code = thrown instanceof MaterialPrepError ? thrown.code : 'unknown';
        setPrepError(t(`error.${code}`));
      }
    } finally {
      if (isLatest()) {
        setPreparing(undefined);
      }
    }
  };

  const handlePick = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset so picking the same file again fires `onChange`; `file` is captured first.
    e.target.value = '';
    if (!file) {
      return;
    }
    lastFileRef.current = file;
    const language = jobLanguage ?? 'sv';
    setPageLanguage(language);
    setPageLanguageShown(false);
    void run(file, language);
  };

  /** Reads the last picked file again in the new page language, from hashing on (PHO-D1). */
  const handlePageLanguageChange = (language: Language) => {
    setPageLanguage(language);
    // The run clears the value, whose OCR pages may be all that shows the select (PHO-D1).
    setPageLanguageShown(true);
    const file = lastFileRef.current;
    if (file) {
      void run(file, language);
    }
  };

  const numberFormat = new Intl.NumberFormat(i18n.language);
  const materialErrors = [prepError, error].filter(
    (message, index, all): message is string => !!message && all.indexOf(message) === index
  );
  const materialHasError = materialErrors.length > 0;
  const displayName = preparing?.name ?? value?.name;

  const parsed = value ? parseRange(range, value.pdfPageCount) : undefined;
  const rangeChars = value && parsed ? charsInRange(value.pageChars, parsed.from, parsed.to) : 0;
  const rangeError = !value
    ? undefined
    : !parsed
      ? t('error.InvalidPageRange')
      : rangeChars > MATERIAL_MAX_CHARS
        ? t('error.MaterialTooLarge')
        : rangeChars === 0
          ? t('error.MaterialEmpty')
          : undefined;

  const status = preparing?.status;
  const progress = status?.progress;
  const extracting =
    status?.phase === 'extracting' && progress && 'pdfPageCount' in progress ? progress : undefined;
  const reading =
    status?.phase === 'reading' && progress && 'total' in progress ? progress : undefined;
  const statusText = !status
    ? undefined
    : extracting
      ? t('job.new.pdf.extracting', { page: extracting.page, count: extracting.pdfPageCount })
      : reading
        ? t('job.new.pdf.reading', {
            page: Math.min(reading.done + 1, reading.total),
            count: reading.total,
          })
        : t(`job.new.pdf.${status.phase}`);
  const percent = extracting
    ? Math.round((extracting.page / extracting.pdfPageCount) * 100)
    : reading
      ? Math.round((reading.done / reading.total) * 100)
      : undefined;
  const ocrPageCount = value?.ocrPages?.length ?? 0;
  const showPageLanguage = pageLanguageShown || ocrPageCount > 0;

  return (
    <>
      <div>
        <label htmlFor="job-material" className="form-label">
          {t('job.field.material')}
        </label>
        <input
          ref={fileInputRef}
          type="file"
          id="job-material"
          accept="application/pdf,.pdf"
          className={[
            'form-control',
            displayName ? 'd-none' : undefined,
            materialHasError ? 'is-invalid' : undefined,
          ]
            .filter(Boolean)
            .join(' ')}
          aria-describedby={materialHasError ? 'job-material-error' : undefined}
          disabled={disabled}
          onChange={handlePick}
        />
        {displayName && (
          <div className="qz-file">
            <Icon name="file-pdf" />
            <div className="flex-grow-1">
              <div className="fw-semibold">{displayName}</div>
              {statusText && <div className="qz-small qz-muted">{statusText}</div>}
              {!preparing && value && (
                <div className="qz-small qz-muted">
                  {t('job.new.pdf.summary', {
                    pages: numberFormat.format(value.pdfPageCount),
                    chars: numberFormat.format(
                      charsInRange(value.pageChars, 1, value.pdfPageCount)
                    ),
                  })}
                  {/* `job.new.pdf.ocrSummary` has no plural forms, so its count is formatted. */}
                  {ocrPageCount > 0 &&
                    ` · ${t('job.new.pdf.ocrSummary', { count: numberFormat.format(ocrPageCount) })}`}
                </div>
              )}
            </div>
            {preparing ? (
              <span className="spinner-border spinner-border-sm" aria-hidden="true" />
            ) : (
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                disabled={disabled}
                onClick={() => fileInputRef.current?.click()}
              >
                {t('job.new.replace')}
              </button>
            )}
          </div>
        )}
        {percent !== undefined && (
          <div
            className="progress mt-2"
            role="progressbar"
            aria-label={statusText}
            aria-valuenow={percent}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <div className="progress-bar" style={{ width: `${percent}%` }} />
          </div>
        )}
        <div className="form-text">{t('job.new.materialHelp')}</div>
        {materialHasError && (
          <div id="job-material-error" className="invalid-feedback d-block">
            {materialErrors.map((message) => (
              <div key={message}>{message}</div>
            ))}
          </div>
        )}
      </div>

      {showPageLanguage && (
        <div>
          <label htmlFor="job-page-language" className="form-label">
            {t('job.new.pageLanguage')}
          </label>
          <select
            id="job-page-language"
            className="form-select"
            aria-describedby="job-page-language-help"
            disabled={disabled}
            value={pageLanguage}
            onChange={(e) => handlePageLanguageChange(e.target.value as Language)}
          >
            {LANGUAGES.map((code) => (
              <option key={code} value={code}>
                {t(`task.language.${code}`)}
              </option>
            ))}
          </select>
          <div id="job-page-language-help" className="form-text">
            {t('job.new.pageLanguageHelp')}
          </div>
        </div>
      )}

      {value && !preparing && (
        <div>
          {/* Text inputs, not number ones: a number input reports unparsable text such as
              "12-" as '', which `parseRange` would read as the PDF's edge. */}
          <div className="row g-3">
            <div className="col-sm-4">
              <label htmlFor="job-page-from" className="form-label">
                {t('job.new.pdf.pageFrom')}
              </label>
              <input
                id="job-page-from"
                type="text"
                inputMode="numeric"
                className={`form-control${rangeError ? ' is-invalid' : ''}`}
                aria-describedby={rangeError ? 'job-page-range-error' : undefined}
                disabled={disabled}
                value={range.from}
                onChange={(e) => onRangeChange({ ...range, from: e.target.value })}
              />
            </div>
            <div className="col-sm-4">
              <label htmlFor="job-page-to" className="form-label">
                {t('job.new.pdf.pageTo')}
              </label>
              <input
                id="job-page-to"
                type="text"
                inputMode="numeric"
                className={`form-control${rangeError ? ' is-invalid' : ''}`}
                aria-describedby={rangeError ? 'job-page-range-error' : undefined}
                disabled={disabled}
                value={range.to}
                onChange={(e) => onRangeChange({ ...range, to: e.target.value })}
              />
            </div>
          </div>
          <div className="form-text">
            {t('job.new.pdf.rangeHelp')}{' '}
            {parsed && t('job.new.pdf.charsInRange', { chars: numberFormat.format(rangeChars) })}
          </div>
          {rangeError && (
            <div id="job-page-range-error" className="invalid-feedback d-block">
              {rangeError}
            </div>
          )}

          <PagePreview key={value.extractionId} material={value} defaultPage={parsed?.from ?? 1} />
        </div>
      )}
    </>
  );
};
